import { StrictMode, startTransition } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { StartClient } from '@tanstack/react-start/client'
import { isHydrationError } from './lib/hydration-mismatch'
import { markHydrationStarted } from './lib/hydration-rewrite-monitor'
import { logFrontendError } from './lib/log-error'

/**
 * Custom client entry (auto-detected as `src/client`, replacing the package default) whose only reason to exist
 * is passing React's error hooks to `hydrateRoot`. The rest is a verbatim copy of upstream's — keep it that way.
 */

/**
 * Replacing React's default `onRecoverableError` also replaces its `reportError` call, so a recoverable error
 * (above all a hydration mismatch) is reported once — here, with the `componentStack` prod minification loses.
 */
function reportWithComponentStack(
  resolvePrefix: (message: string) => string,
  error: unknown,
  errorInfo: { componentStack?: string | null },
): void {
  const message = error instanceof Error ? error.message : String(error)
  const namePrefix = resolvePrefix(message)
  console.error(namePrefix, error, errorInfo.componentStack)
  logFrontendError(error, 'recoverable', { namePrefix: `${namePrefix} `, componentStack: errorInfo.componentStack })
}

function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | null }): void {
  reportWithComponentStack((message) => (isHydrationError(message) ? '[HYDRATION]' : '[RECOVERABLE]'), error, errorInfo)
}

/**
 * The same replacement for errors no boundary caught. `[UNCAUGHT]` stays its own prefix because the name feeds
 * the server's error fingerprint, and a page React could not recover is a different severity from a patched one.
 */
function reportUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }): void {
  reportWithComponentStack(() => '[UNCAUGHT]', error, errorInfo)
}

// Outside the transition and before it: every rewrite the monitor logs from here on could be React's own.
markHydrationStarted()

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <StartClient />
    </StrictMode>,
    // `onCaughtError` stays at React's default: the boundary that caught it already reports it (route-error.tsx).
    { onRecoverableError: reportRecoverableError, onUncaughtError: reportUncaughtError },
  )
})
